import { useCallback, useEffect, useMemo, useState } from "react";
import {
    personalizedSearchIndiciesByCompany,
    allowedSuggestionsIndices, defaultSearchData, featureFlagIndicesData
} from "../indices/indices.config";
import { getFrom } from "../../../util/map";
import { SearchableIndexKey, SearchableIndexValue, SourceNameWithIndices } from "../indices/indices.types";
import { useUserData } from "@me8eon/react_authentication";
import { useFeatureFlag } from "@me8eon/featureflags";
import { EnvironmentConfig } from "@me8eon/config";
import { isCompassFeatureFlagEnabled } from "@me8eon/compass_data_plugin";

export type ChosenChatbotSource = SearchableIndexKey | "All sources";

/**
 * Custom hook to manage chatbot indices for search and personalization.
 * This hook provides functionality to handle active indices,
 * available indices, and the ability to change active indices based on user input.
 */
export const useChatbotIndicesHook = () => {
    const { company, featureFlagEnabled, empType } = useUserData();
    const myHrFeatureFF = useFeatureFlag("myhr")
    const hrPoliciesFeatureFF = useFeatureFlag("hrPolicies");
    const compassFeatureFF = isCompassFeatureFlagEnabled(empType);
    const snowProdFlag = EnvironmentConfig.isHigherEnv && !featureFlagEnabled;

    const availableSearchIndices: SourceNameWithIndices = useMemo(() => {
        const indices = new Map()
        Object.entries(defaultSearchData).forEach(([sourceName, indicesArray]) => {
            indices.set(sourceName, indicesArray);
        });
        Object.entries(featureFlagIndicesData[snowProdFlag ? "snowprod" : "snowdev"]).forEach(([sourceName, indicesArray]) => {
            indices.set(sourceName, indicesArray);
        });
        if (myHrFeatureFF) {
            Object.entries(featureFlagIndicesData["myhr"]).forEach(([sourceName, indicesArray]) => {
                indices.set(sourceName, indicesArray);
            });
        }
        if (hrPoliciesFeatureFF && company && company.toLowerCase().includes("digital technology")) {
            Object.entries(featureFlagIndicesData["hrPolicies"]).forEach(([sourceName, indicesArray]) => {
                indices.set(sourceName, indicesArray);
            });
        }
        if (compassFeatureFF) {
            Object.entries(featureFlagIndicesData["compass"]).forEach(([sourceName, indicesArray]) => {
                indices.set(sourceName, indicesArray);
            });
        }

        return indices
    }, [myHrFeatureFF, hrPoliciesFeatureFF, company, compassFeatureFF]);

    const availablePersonalizedIndices = useMemo(() => {
        return getFrom(personalizedSearchIndiciesByCompany)(company)
    }, [company]) as SourceNameWithIndices

    // Didn't initialize default value due to re-render issue for "company" value. Values will be assigned later on in useEffect.
    const [activePersonalizedIndices, setActivePersonalizedIndices] = useState<SourceNameWithIndices>(new Map());
    const [activeSearchIndices, setActiveSearchIndices] = useState<SourceNameWithIndices>(new Map());
    const [chosenSource, setChosenSource] = useState<ChosenChatbotSource>("All sources");
    const [filteredIndices, setFilteredIndices] = useState<string[] | null>(null);

    const [activeSuggestionIndices, setActiveSuggestionIndices] = useState<string[]>([]);

    const handleChangeChosenSource = useCallback((source: ChosenChatbotSource, filteredIndex?: string[]) => {
        setChosenSource(source);
        filteredIndex ? setFilteredIndices(filteredIndex) : setFilteredIndices(null);
    }, [setChosenSource]);

    useEffect(() => {
        if (chosenSource === "All sources") {
            setActiveSearchIndices(availableSearchIndices);
            setActivePersonalizedIndices(availablePersonalizedIndices);
            return;
        }
        const availableIndices = new Map(availableSearchIndices);

        let newActiveSearchIndices: SourceNameWithIndices = new Map();
        let newActivePersonalizedIndices: SourceNameWithIndices = new Map();

        if (featureFlagEnabled) {
            (Object.entries(featureFlagIndicesData["betaUserFlag"]) as [SearchableIndexKey, readonly SearchableIndexValue[]][]).forEach(([sourceName, indicesArray]) => {
                availableIndices.set(sourceName, [...indicesArray]);
            });
        }

        if (availableIndices.has(chosenSource)) {
            newActiveSearchIndices.set(chosenSource, availableIndices.get(chosenSource) || []);
        }
        if (availablePersonalizedIndices.has(chosenSource)) {
            newActivePersonalizedIndices.set(chosenSource, availablePersonalizedIndices.get(chosenSource) || []);
        }
        if (filteredIndices) {
            console.log("to filter:", newActiveSearchIndices);
            console.log("With filtered indices:", filteredIndices);
            // newActiveSearchIndices = newActiveSearchIndices.filter((indices: string) => { filteredIndices.includes(indices) });
        }
        setActiveSearchIndices(newActiveSearchIndices);
        setActivePersonalizedIndices(newActivePersonalizedIndices);
    }, [chosenSource, availablePersonalizedIndices, availableSearchIndices, setActivePersonalizedIndices, setActiveSearchIndices]);

    // Initialize active indices with available indices
    useEffect(() => {
        setActivePersonalizedIndices(availablePersonalizedIndices);
        setActiveSearchIndices(availableSearchIndices);
    }, [availablePersonalizedIndices, availableSearchIndices]);

    // Update active suggestion indices based on active search and personalized indices
    useEffect(() => {
        const availableIndices = [...activeSearchIndices.values(), ...activePersonalizedIndices.values()].flat();
        setActiveSuggestionIndices(availableIndices.filter(index => allowedSuggestionsIndices.includes(index)));
    }, [activeSearchIndices, activePersonalizedIndices]);

    const availableSourceNames: SearchableIndexKey[] = useMemo(() => [...availableSearchIndices.keys(), ...availablePersonalizedIndices.keys()], [availableSearchIndices, availablePersonalizedIndices]);

    const activeSourceNames: SearchableIndexKey[] = useMemo(() => [...activeSearchIndices.keys(), ...activePersonalizedIndices.keys()], [activeSearchIndices, activePersonalizedIndices]);

    return {
        availableSearchIndices,
        availablePersonalizedIndices,
        activeSearchIndices,
        activePersonalizedIndices,
        handleChangeChosenSource,
        availableSourceNames,
        activeSourceNames,
        activeSuggestionIndices,
        chosenSource,
        setChosenSource
    }
}